import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, Box, Typography, IconButton,
    Grid, CircularProgress, Alert, Tooltip, useMediaQuery, useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import GridViewIcon from '@mui/icons-material/GridView';
import AuthenticatedImage from '../AuthenticatedImage';

const serverApiUrl = process.env.REACT_APP_SERVER_API || 'http://localhost:9000/';

const PatientImageViewer = ({ open, onClose, studyInstanceUID }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const lightboxRef = useRef(null);

    const [instances, setInstances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null); // null = grid view, number = lightbox
    const [zoom, setZoom] = useState(1);

    // Fetch instance list when dialog opens
    useEffect(() => {
        if (open && studyInstanceUID) {
            fetchInstances();
        }
        if (!open) {
            setInstances([]);
            setSelectedIndex(null);
            setZoom(1);
            setError(null);
        }
    }, [open, studyInstanceUID]);

    const fetchInstances = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('item');
            const response = await fetch(
                `${serverApiUrl}api/dicom/patient/study-images/${encodeURIComponent(studyInstanceUID)}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            const data = await response.json();
            if (data.success) {
                setInstances(data.data?.instances || []);
            } else {
                setError(data.message || 'Failed to load images');
            }
        } catch (err) {
            console.error('Error fetching study instances:', err);
            setError('Failed to load images. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleThumbnailClick = (index) => {
        setSelectedIndex(index);
        setZoom(1);
    };

    const handleBackToGrid = () => {
        setSelectedIndex(null);
        setZoom(1);
    };

    const handlePrev = useCallback(() => {
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : instances.length - 1));
        setZoom(1);
    }, [instances.length]);

    const handleNext = useCallback(() => {
        setSelectedIndex(prev => (prev < instances.length - 1 ? prev + 1 : 0));
        setZoom(1);
    }, [instances.length]);

    const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.5, 4));
    const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.5, 0.5));

    const handleFullscreen = () => {
        if (lightboxRef.current?.requestFullscreen) {
            lightboxRef.current.requestFullscreen();
        }
    };

    // Keyboard navigation
    useEffect(() => {
        if (selectedIndex === null) return;

        const handleKeyDown = (e) => {
            switch (e.key) {
                case 'ArrowLeft':
                    handlePrev();
                    break;
                case 'ArrowRight':
                    handleNext();
                    break;
                case 'Escape':
                    handleBackToGrid();
                    break;
                case '+':
                case '=':
                    handleZoomIn();
                    break;
                case '-':
                    handleZoomOut();
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedIndex, handlePrev, handleNext]);

    // Mouse wheel zoom in lightbox
    const handleWheel = useCallback((e) => {
        if (selectedIndex === null) return;
        e.preventDefault();
        setZoom(prev => {
            const delta = e.deltaY > 0 ? -0.25 : 0.25;
            return Math.max(0.5, Math.min(4, prev + delta));
        });
    }, [selectedIndex]);

    const getPreviewUrl = (instanceId) => {
        return `${serverApiUrl}api/dicom/patient/instances/${instanceId}/preview`;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            PaperProps={{
                sx: { bgcolor: '#0a0a0a' }
            }}
        >
            {/* Header */}
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                bgcolor: '#111',
                color: '#fff',
                py: 1,
                px: 2,
                borderBottom: '1px solid #333',
                minHeight: 48
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selectedIndex !== null && (
                        <Tooltip title="Back to grid">
                            <IconButton onClick={handleBackToGrid} sx={{ color: '#fff' }} size="small">
                                <GridViewIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Typography variant="body1" fontWeight={600}>
                        {selectedIndex !== null
                            ? `Image ${selectedIndex + 1} of ${instances.length}`
                            : `Study Images (${instances.length})`
                        }
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {selectedIndex !== null && (
                        <>
                            <Tooltip title="Zoom out (-)">
                                <IconButton onClick={handleZoomOut} sx={{ color: '#fff' }} size="small" disabled={zoom <= 0.5}>
                                    <ZoomOutIcon />
                                </IconButton>
                            </Tooltip>
                            <Typography sx={{ color: '#aaa', alignSelf: 'center', fontSize: '0.8rem', minWidth: 40, textAlign: 'center' }}>
                                {Math.round(zoom * 100)}%
                            </Typography>
                            <Tooltip title="Zoom in (+)">
                                <IconButton onClick={handleZoomIn} sx={{ color: '#fff' }} size="small" disabled={zoom >= 4}>
                                    <ZoomInIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Fullscreen">
                                <IconButton onClick={handleFullscreen} sx={{ color: '#fff' }} size="small">
                                    <FullscreenIcon />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    <Tooltip title="Close">
                        <IconButton onClick={onClose} sx={{ color: '#fff' }} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>

            {/* Content */}
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: '#0a0a0a' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                        <CircularProgress sx={{ color: '#fff' }} />
                        <Typography sx={{ color: '#aaa', mt: 2 }}>Loading images...</Typography>
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                ) : instances.length === 0 ? (
                    <Box sx={{ p: 3 }}>
                        <Alert severity="info">No images available for this study.</Alert>
                    </Box>
                ) : selectedIndex !== null ? (
                    /* Lightbox View */
                    <Box
                        ref={lightboxRef}
                        onWheel={handleWheel}
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                            userSelect: 'none',
                        }}
                    >
                        {/* Previous button */}
                        <IconButton
                            onClick={handlePrev}
                            sx={{
                                position: 'absolute',
                                left: { xs: 4, sm: 16 },
                                zIndex: 2,
                                color: '#fff',
                                bgcolor: 'rgba(0,0,0,0.5)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                            }}
                        >
                            <ArrowBackIosNewIcon />
                        </IconButton>

                        {/* Image */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            overflow: 'auto',
                        }}>
                            <AuthenticatedImage
                                src={getPreviewUrl(instances[selectedIndex])}
                                alt={`Image ${selectedIndex + 1}`}
                                style={{
                                    maxWidth: zoom === 1 ? '100%' : 'none',
                                    maxHeight: zoom === 1 ? '100%' : 'none',
                                    width: zoom !== 1 ? `${zoom * 100}%` : 'auto',
                                    objectFit: 'contain',
                                    transition: 'width 0.15s ease',
                                }}
                            />
                        </Box>

                        {/* Next button */}
                        <IconButton
                            onClick={handleNext}
                            sx={{
                                position: 'absolute',
                                right: { xs: 4, sm: 16 },
                                zIndex: 2,
                                color: '#fff',
                                bgcolor: 'rgba(0,0,0,0.5)',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                            }}
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                    </Box>
                ) : (
                    /* Thumbnail Grid View */
                    <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 1, sm: 2 } }}>
                        <Grid container spacing={isMobile ? 0.5 : 1.5}>
                            {instances.map((instanceId, index) => (
                                <Grid item xs={4} sm={3} md={2} key={instanceId}>
                                    <Box
                                        onClick={() => handleThumbnailClick(index)}
                                        sx={{
                                            cursor: 'pointer',
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            border: '2px solid transparent',
                                            transition: 'border-color 0.2s',
                                            '&:hover': { border: '2px solid', borderColor: 'primary.main' },
                                            bgcolor: '#000',
                                            aspectRatio: '1',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <AuthenticatedImage
                                            src={getPreviewUrl(instanceId)}
                                            alt={`Image ${index + 1}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                display: 'block',
                                            }}
                                        />
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PatientImageViewer;
